import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, GraduationCap, CheckCircle2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal, Stagger, staggerItem } from "../components/Motion";
import { fetchPrograms } from "../lib/api";

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrograms()
      .then(setPrograms)
      .finally(() => setLoading(false));
  }, []);

  const active = programs.filter((p) => p.status === "active");
  const upcoming = programs.filter((p) => p.status === "coming_soon");

  return (
    <div data-testid="programs-page">
      <PageHeader
        kicker="Programs"
        title="Career-focused programs designed for Canada's economy."
        subtitle="Heritage starts strong with our Diploma in Business Management — and we're rapidly expanding our catalogue to meet the careers Ontario employers are hiring for."
      />

      <section className="hccbt-container section pt-0">
        <div className="mb-10">
          <span className="overline">Now Enrolling</span>
          <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight text-[#0F172A]">Active Programs</h2>
        </div>
        {loading && <p className="text-[#64748B]">Loading programs…</p>}
        <Stagger className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {active.map((p) => (
            <motion.article
              key={p.id}
              variants={staggerItem}
              whileHover={{ y: -6 }}
              className="card-soft card-lift p-8 flex flex-col"
              data-testid={`program-active-${p.id}`}
            >
              <div className="flex items-center gap-2">
                <span className="kicker">Now enrolling</span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl mt-5 text-[#0F172A] leading-snug">{p.title}</h3>
              <p className="mt-4 text-[#475569] leading-relaxed">{p.summary}</p>

              <div className="mt-6 flex flex-wrap gap-4 text-sm text-[#475569]">
                <span className="inline-flex items-center gap-2"><Clock size={16} className="text-[#6B9AC4]" /> {p.duration}</span>
                <span className="inline-flex items-center gap-2"><GraduationCap size={16} className="text-[#6B9AC4]" /> {p.credential}</span>
              </div>

              {p.highlights?.length > 0 && (
                <ul className="mt-6 space-y-2">
                  {p.highlights.slice(0, 3).map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-[#334155]">
                      <CheckCircle2 size={16} className="text-[#6B9AC4] mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={`/programs/${p.slug}`} className="btn-primary" data-testid={`program-detail-${p.id}`}>
                  Program details <ArrowRight size={16} />
                </Link>
                <Link to="/admissions" className="btn-secondary">How to apply</Link>
              </div>
            </motion.article>
          ))}
        </Stagger>
      </section>

      <section className="bg-white border-y border-[#E2E8F0]">
        <div className="hccbt-container section">
          <div className="mb-10">
            <span className="overline">Coming Soon · 2026</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight text-[#0F172A]">
              Programs in development
            </h2>
            <p className="mt-4 text-[#475569] max-w-2xl">
              We are carefully building each new program with industry partners. Join the waitlist for early access to applications and scholarship opportunities.
            </p>
          </div>
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcoming.map((p) => (
              <motion.article
                key={p.id}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                className="card-soft card-lift p-6"
                data-testid={`program-upcoming-${p.id}`}
              >
                <span className="text-xs uppercase tracking-widest font-semibold text-[#F59E0B]">Coming Soon</span>
                <h3 className="font-display text-xl mt-3 leading-snug text-[#0F172A]">{p.title}</h3>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">{p.summary}</p>
                <div className="mt-4 flex items-center gap-2 text-xs text-[#64748B]">
                  <Clock size={14} /> {p.duration} · {p.credential}
                </div>
                <Link to="/contact" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#6B9AC4] group" data-testid={`waitlist-${p.id}`}>
                  Join waitlist <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.article>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
};

export default Programs;
